import {
  Field,
  FieldContent,
  FieldLabel,
  RadioGroup,
  RadioGroupItem,
} from "@design-system/shadcn";
import { pickDataAttributes, type DataAttributes } from "@design-system/utils";
import React, { useCallback } from "react";

import { useFieldContext } from "../core/form-contexts";
import { useFieldDescribedBy } from "../core/use-field-described-by";

interface Option {
  value: string;
  label: string;
  dataAttributes?: DataAttributes;
}

interface FormFieldRadioGroupProps extends Omit<
  React.ComponentProps<typeof RadioGroup>,
  "name" | "value" | "onValueChange"
> {
  options: Option[];
}

function FormFieldRadioGroup({ options, ...props }: FormFieldRadioGroupProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error("FormFieldRadioGroup must be used within form.AppField");
  }

  const changeHandler = useCallback(
    (value: string) => field.handleChange(value),
    [field]
  );

  const hasError = field.state.meta.errors.length > 0;

  const describedBy = useFieldDescribedBy(props["aria-describedby"]);

  return (
    <RadioGroup
      name={field.name}
      aria-labelledby={`${field.name}-label`}
      aria-invalid={hasError}
      value={field.state.value}
      onValueChange={changeHandler}
      {...props}
      aria-describedby={describedBy}
    >
      {options.map((option) => {
        const dataAttributes = pickDataAttributes(option.dataAttributes ?? {});

        const optionId = `${field.name}-${option.value}`;

        return (
          <Field
            orientation="horizontal"
            key={option.value}
            data-disabled={props.disabled || undefined}
          >
            <RadioGroupItem
              {...dataAttributes}
              value={option.value}
              id={optionId}
            />
            <FieldContent>
              <FieldLabel {...dataAttributes} htmlFor={optionId}>
                {option.label}
              </FieldLabel>
            </FieldContent>
          </Field>
        );
      })}
    </RadioGroup>
  );
}

export { FormFieldRadioGroup };
export type { FormFieldRadioGroupProps, Option };
